import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import ProductImage, { isCataloguePhoto } from "./ProductImage";
import { NAVY, BLUE, MUTED, BORDER } from "./productTheme";
import { CT } from "./catalogueTheme";

const SIZES = {
  large: { imgH: "h-[300px] lg:h-[340px]", pad: "p-8", title: "text-2xl lg:text-[26px]", desc: "text-base line-clamp-3" },
  default: { imgH: "h-[220px] lg:h-[240px]", pad: "p-6", title: "text-xl lg:text-[22px]", desc: "text-[15px] line-clamp-2" },
  compact: { imgH: "h-[190px] lg:h-[200px]", pad: "p-5", title: "text-lg", desc: "text-sm line-clamp-2" },
};

/**
 * Premium product card for the category/product grid on /products —
 * selects a product in place (calls `onSelect`) instead of navigating away,
 * so it fits the "stay inside the category experience" flow. Built fresh in
 * Shingare's own navy/blue palette rather than reusing FeaturedProducts.jsx
 * (Home page), which uses a different "sky blue" palette — keeping this
 * contained to /products avoids any risk to the Home page.
 *
 * Image is full-bleed (object-cover, no inner padding/frame) instead of a
 * contained image floating inside a padded gray box — the source photography
 * is wide cinematic hero-banner shots (~2.4:1, product often off-center
 * against a dark background), which is exactly what made `object-contain`
 * read as "huge empty space in the card": the whole banner got letterboxed
 * in, shrinking the actual product to a small fraction of the frame. A
 * fixed-height `object-cover` center crop can't be perfect for every source
 * image without per-image focal-point data (not in Admin), but it reliably
 * fills the card and reads far stronger than the letterboxed version.
 *
 * `size`: "large" (1–2 product feature sections), "default" (4-col grid),
 * "compact" (Related Products — deliberately smaller than the main grid).
 * `h-full` + `flex-col` + `mt-auto` on the CTA keep every card in a row the
 * same height regardless of description length.
 */
export default function ProductCard({ product, onSelect, active = false, ctaLabel = "View Product", categoryLabel, size = "default", variant = "default", href }) {
  if (variant === "catalogue") {
    return <CatalogueCard product={product} onSelect={onSelect} active={active} ctaLabel={ctaLabel} categoryLabel={categoryLabel} href={href} />;
  }
  const label = categoryLabel ?? product.category;
  const s = SIZES[size] || SIZES.default;
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={`product-card-${product.slug}`}
      className={`group h-full w-full flex flex-col text-left bg-white border rounded-2xl overflow-hidden shadow-[0_2px_10px_-4px_rgba(11,31,77,0.12)] hover:shadow-[0_20px_40px_-16px_rgba(11,31,77,0.35)] hover:-translate-y-1 transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] ${
        active ? "ring-2 ring-[#2563EB]" : ""
      }`}
      style={{ borderColor: "#E5EAF1" }}
    >
      <div className={`relative ${s.imgH} overflow-hidden bg-[#EEF2F7] shrink-0`}>
        <ProductImage src={product.image} alt={product.name} padding={isCataloguePhoto(product.image) ? "p-2" : "p-0"} />
      </div>
      <div className={`flex flex-col flex-1 ${s.pad}`}>
        {label && (
          <div className="text-xs font-heading font-bold uppercase tracking-widest mb-2.5" style={{ color: BLUE }}>{label}</div>
        )}
        <h3 className={`font-heading font-bold leading-tight mb-2.5 ${s.title}`} style={{ color: NAVY }}>{product.name}</h3>
        {product.short_description && (
          <p className={`leading-relaxed mb-5 flex-1 ${s.desc}`} style={{ color: MUTED }}>{product.short_description}</p>
        )}
        <div className="flex items-center gap-2 text-sm font-semibold mt-auto" style={{ color: BLUE }}>
          {ctaLabel} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </button>
  );
}

/**
 * `variant="catalogue"` — the light industrial-catalogue card used on the
 * subcategory listing. Same data contract as the default card (product,
 * onSelect, active, ctaLabel, categoryLabel); the default card above is left
 * exactly as it was for the pages that still use it. When `href` is given the
 * card is a real link (open-in-new-tab, crawlable); otherwise it falls back to
 * a button that calls `onSelect`.
 *
 * Fixed 4:3 image frame + `h-full` flex column + `mt-auto` CTA keep every card
 * in a row the same height regardless of name/description length. Hover: card
 * lifts 4px, image scales ~4.5% (inside ProductImage via `group-hover`), CTA
 * arrow nudges right, border/shadow strengthen.
 */
function CatalogueCard({ product, onSelect, active, ctaLabel, categoryLabel, href }) {
  const label = categoryLabel ?? product.category;
  // Imported descriptions can start with the source's "»" bullet markers — show them as a clean, dot-separated line.
  const blurb = String(product.short_description || "").replace(/\s*»\s*/g, " · ").replace(/^[\s·]+/, "").trim();
  const className = `group h-full w-full flex flex-col text-left bg-white border rounded-2xl overflow-hidden shadow-[0_1px_2px_rgba(11,33,72,0.04)] hover:-translate-y-1 hover:border-[#BFD3F5] hover:shadow-[0_18px_34px_-18px_rgba(11,33,72,0.32)] transition-[transform,box-shadow,border-color] duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2 ${
    active ? "ring-2 ring-[#2563EB]" : ""
  }`;
  const body = (
    <>
      <div className="relative aspect-[4/3] shrink-0 overflow-hidden border-b" style={{ borderColor: "#EEF1F6" }}>
        <ProductImage src={product.image} alt={product.name} padding={isCataloguePhoto(product.image) ? "p-1" : "p-5"} />
      </div>
      <div className="flex flex-col flex-1 p-5">
        {label && (
          <span className="self-start max-w-full truncate text-[11px] font-bold uppercase tracking-[0.1em] px-2.5 py-1 rounded-md mb-3" style={{ color: CT.blue, backgroundColor: CT.lightBlue }}>{label}</span>
        )}
        {/* lineHeight is set inline, not just via `leading-snug`: index.css's sitewide `h1..h6, .font-heading { line-height: 1.15 }`
            (needed everywhere else headings appear) has equal CSS specificity to a Tailwind utility class and loads after it, so on
            a `.font-heading` element it silently wins the cascade — shrinking each line below what `min-h-[2.9rem]` reserves for
            exactly 2 lines at 1.375, so `line-clamp-2` clips a few pixels into a 3rd (long) title's line instead of hiding it fully. */}
        <h3 className="shrink-0 font-heading font-bold text-[17px] line-clamp-2 min-h-[2.9rem]" style={{ color: CT.navy, lineHeight: 1.375 }}>{product.name}</h3>
        {blurb && (
          <p className="shrink-0 mt-2 text-sm leading-relaxed line-clamp-3" style={{ color: CT.muted }}>{blurb}</p>
        )}
        <span className="mt-auto pt-4 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: CT.blue }}>
          {ctaLabel} <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
        </span>
      </div>
    </>
  );
  const common = { className, style: { borderColor: CT.border }, "data-testid": `product-card-${product.slug}` };
  return href ? (
    <Link to={href} {...common}>{body}</Link>
  ) : (
    <button type="button" onClick={onSelect} {...common}>{body}</button>
  );
}
